import type { Infinite } from "@-/data-loader/infinite.svelte.ts";
import type { Ref, RefKind } from "@-/data-loader/loader.svelte.ts";
import type { Cell } from "@-/data-loader/storage.svelte.ts";
import type { BlueskySession } from "./bluesky/bluesky.ts";
import type { Feed } from "./feeds.ts";
import type { MastodonSession } from "./mastodon/mastodon.ts";
import type { MaybePost, PostId, PostMyInteraction, Thread } from "./posts.ts";
import { type Session, getSessions } from "./sessions.svelte.ts";
import type { TimelineItem } from "./timeline.ts";
import type { UserBasic, UserDetailed, UserRelationship } from "./users.ts";

const clientCache = new Map<string, Client>();

const configured = new Set<string /* network */>();

export const makeClientFor = async (network: string, sessionId?: string): Promise<Client> => {
	const cacheKey = network + "/" + sessionId;

	{
		const cached = clientCache.get(cacheKey);
		if (cached) return cached;
	}

	let session = getSessions().all[network]?.find(s => s.id == sessionId);
	if (!session && sessionId) throw new Error("unrecognized sessionId");

	if (network == "bluesky") {
		const module = await import("./bluesky/bluesky.ts");

		if (!configured.has(network)) {
			module.configure();
			configured.add(network);
		}

		// oxlint-disable-next-line typescript/no-unsafe-type-assertion
		const client = await module.login(session as unknown as BlueskySession);
		clientCache.set(cacheKey, client);
		return client;
	}

	const [networkId, instance] = network.split(":", 2);
	if (networkId == "masto" && instance) {
		const module = await import("./mastodon/mastodon.ts");

		if (!configured.has(network)) {
			module.configure();
			configured.add(network);
		}

		// the instance name is copied in the session
		session ??= { id: "logged-out", instance } satisfies MastodonSession | Session;

		// oxlint-disable-next-line typescript/no-unsafe-type-assertion
		const client = await module.login(session as unknown as MastodonSession);
		clientCache.set(cacheKey, client);
		return client;
	}

	throw new Error("unrecognized network: " + network);
};

export class LoadingError extends Error {
	status: number;
	error: string;
	details?: string;

	constructor(status: number, error: string, details?: string) {
		super(status + " " + error + (details ? ": " + details : undefined));

		this.status = status;
		this.error = error;
		this.details = error;
	}
}

export interface Client {
	network: string;
	me: Ref<UserDetailed> | undefined;

	userById: RefKind<UserDetailed>;
	userBasicById: RefKind<UserBasic>;
	userByHandle: RefKind<Ref<UserDetailed>>;
	userRelationship: RefKind<UserRelationship>;

	postMyInteraction(id: PostId): Cell<PostMyInteraction> | undefined;
	postThread: RefKind<Thread, PostId>;
	postById: RefKind<MaybePost, PostId>;

	feeds: RefKind<Feed[], void>;

	homeTl: RefKind<Infinite<any, TimelineItem>, void>;

	notifCount: RefKind<number, void>;
}
